import Constants from "./constants";
import { OtherIncome } from "./otherIncome";
import OtherIncomeType from "./otherIncomeType";
import { Retiree } from "./retiree";
import RetireeType from "./retireeType";
import Browser from "../../shared/ts/browser";
import store from "./store";
import Temporal from "../../shared/ts/temporal";
import User from "./user";
import EditMode from "./editMode";
import PermissionSet from "./permissionSet";
import { RetirementPlanResponse } from "./response/retirementPlanResponse";
import ExternalAgency from "./consent/externalAgency";
import ConsentType from "./consent/consentType";
import PermittedEntityType from "./consent/permittedEntityType";
import KeyValue from "./common/keyValue";
import DataResult from "./dataResult";

export const generateUUID = function (): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0,
            v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

export const getOtherIncomeName = function (otherIncome: OtherIncome): string {
    return otherIncome.otherIncomeType === OtherIncomeType.AnnualAnnuity
        ? "Annuity Income"
        : otherIncome.name;
}

export const isImpersonating = function (): boolean {
    return store.state.impersonatingId != null;
}

export const isAdminUser = function (): boolean {
    const user = store.state.user as User;
    for (const adminRole of Constants.AdminRoles) {
        if (user.isInRole(adminRole)) {
            return true;
        }
    }
    return false;
}
export const getAgeAtRetirement = function (): number | null {
    if (store.state.retiree == null) {
        return null;
    }

    const dateOfBirth = new Date(store.state.retiree.dateOfBirth);
    const retirementDate = new Date(store.state.retiree.retirementDate);

    return Temporal.calcAgeAtDate(dateOfBirth, retirementDate);
}

export const getAgeNow = function (): number | null {
    if (store.state.retiree == null) {
        return null;
    }

    const dateOfBirth = new Date(store.state.retiree.dateOfBirth);
    return Temporal.calcAgeAtDate(dateOfBirth, new Date());
}

export const getAgeByPlanYear = function (planYear: number): number | null {
    const ageAtRetirement = getAgeAtRetirement();
    if (ageAtRetirement == null) {
        return null;
    }
    return ageAtRetirement + planYear;
}

export const getYearsFromRetirementDate = function (): number | null {
    if (store.state.retiree == null) {
        return null;
    }

    const now = new Date();
    const retirementDate = new Date(store.state.retiree.retirementDate);

    const yearsBetween = Temporal.yearsBetweenDatesExact(now, retirementDate);

    return now < retirementDate ? -1 * yearsBetween : yearsBetween;
}

export const getRetireeType = function (retiree: Retiree): RetireeType {
    if (retiree == null) {
        return RetireeType.Planner;
    }

    return retiree.alreadyDrawingPension ? RetireeType.Manager : RetireeType.Planner;
}

export const hasAnyAssets = function (retiree: Retiree): boolean {
    if (retiree.pensionPots.valueAtRetirement > 0) {
        return true;
    }

    if (retiree.finalSalaries.length > 0) {
        return true;
    }

    if (retiree.savingsPots.pots.length > 0) {
        return true;
    }

    if (retiree.otherCash.length > 0) {
        return true;
    }

    if (retiree.otherIncome.length > 0) {
        return true;
    }

    return false;
}

export const presentValueOfPensionsAndSavings = function (): number {
    if (store.state.retiree == null) {
        return 0;
    }

    const retiree = store.state.retiree;
    return retiree.pensionPots.presentValue + retiree.savingsPots.presentValue;
}

export const isJanuaryFirst = function (date: Date): boolean {
    return date.getMonth() === 0 && date.getDate() === 1;
}

export const adjustForInflationBetweenDates = function (amount: number, valuationDate: Date, revaluationDate: Date): number {
    const yearsToApplyInflation = getDecimalYearDifference(valuationDate, revaluationDate);
    return adjustForInflation(amount, yearsToApplyInflation);
}

export const adjustForInflation = function (amount: number, yearsToApplyInflation: number): number {
    const inflationRate = 1.025;
    const inflationFactor = Math.pow(inflationRate, yearsToApplyInflation);
    const adjusted = amount * inflationFactor;
    return adjusted;
}

export const round = function (value: number, unit: number): number {
    return Math.round(value / unit) * unit;
}

export const roundUp = function (value: number, unit: number): number {
    return Math.ceil(value / unit) * unit;
}

export const roundUpToNearest = function (value: number, possibleValues: number[]): number {
    const sortedValues = possibleValues.sort((a, b) => a - b);
    const closestValue = sortedValues.find((v) => v >= value) || sortedValues[sortedValues.length - 1];
    return closestValue;
}

export const yesNoOptions = ["Yes", "No"];

export const mapYesNoToBoolean = function (yesNo: string): boolean {
    return yesNo.toLowerCase() == "yes";
}

export const mapBooleanToYesNo = function (boolValue: boolean): string {
    return boolValue ? "Yes" : "No";
}

export const getYesNoWidth = function (): number {
    return Browser.isMobile() ? 50 : 25;
}

export const isNullOrWhitespace = function (str: string | null | undefined): boolean {
    return str === null || str === undefined || str.trim() === '';
}

export const getMonthName = function (month: number): string {
    return Intl.DateTimeFormat("en", { month: "long" }).format(
        new Date(2000, month - 1, 1)
    );
}

export const decodeEncodedHtml = function (encoded: string) {
    const textArea = document.createElement("textarea");
    textArea.innerHTML = encoded;
    return textArea.value;
}

export const getPermissionSet = function (editMode: EditMode): PermissionSet {
    const canView = editMode === EditMode.ReadOnly;
    const canEdit = editMode === EditMode.Write;
    const canDelete = editMode === EditMode.Write || editMode === EditMode.DeleteOnly;
    return new PermissionSet(canView, canEdit, canDelete);
}

export const getPensionPotDrawdownAmounts = function (plan: RetirementPlanResponse): Array<number> {
    const amounts = new Array<number>();

    if (!plan) {
        return amounts;
    }

    for (const year of plan.years) {
        amounts.push(year.takeFromPensions);
    }

    return amounts;
}

export const hasConsented = function(externalAgency: ExternalAgency, consentType: ConsentType): boolean {
    const retiree = store.state.retiree;

    for (const consent of retiree.consents) {
        if (consent.permittedEntityType != PermittedEntityType.ExternalAgency) {
            continue;
        }

        if (consent.permittedEntityId != externalAgency) {
            continue;
        }

        if (consent.type == consentType) {
            return true;
        }
    }

    return false;
}

export const camelCaseToHumanReadable = function(text: string): string {
    return text.replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase();
}

export const ensureScriptLoaded = function(src: string, onloadCallback?: () => void) {
    let script = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);

    if (script) {
        if ((script as any).__loaded) {
            onloadCallback?.();
        } else if (onloadCallback) {
            script.addEventListener(
                'load',
                () => {
                (script as any).__loaded = true;
                onloadCallback();
                },
                { once: true }
            );
        }
        return;
    }

    script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.async = true;

    const nonce = getNonce();
    script.setAttribute('nonce', nonce);

    if (onloadCallback) {
        script.addEventListener(
            'load',
            () => {
                (script as any).__loaded = true;
                onloadCallback();
            },
            { once: true }
        );
    }

    document.head.appendChild(script);
}

export const ensureScriptUnloaded = function(src: string) {
    const existingScript = document.querySelector(`script[src="${src}"]`);

    if (existingScript) {
        existingScript.remove();
    }
}

export const ensureCssLoaded = (href: string): void => {
    if (document.querySelector(`link[href="${href}"]`)) {
        return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = href;
    link.media = 'all';

    const nonce = getNonce();
    link.setAttribute('nonce', nonce);

    document.head.appendChild(link);
};

export const getNonce = (): string => {
    const nonceElement = document.querySelector('meta[name="csp-nonce"]');

    if (nonceElement && nonceElement.getAttribute('content')) {
        return nonceElement.getAttribute('content');
    }

    return "";
}

export const ensureMetaTagPresent = (name: string, content: string): void => {
    if (document.querySelector(`meta[name="${name}"]`)) {
        return;
    }

    const meta = document.createElement('meta');
    meta.name = name;
    meta.content = content;

    document.head.appendChild(meta);
};

export const updateCanonicalTag = function(href: string) {
    if (!href) {
        return;
    }

    const cleanHref = href.replace(/\/$/, '');

    updateLinkTag('canonical', cleanHref);
}

export const singularOrPlural = function(word: string, count: number): string {
    let plural = word;
    
    if (word.endsWith('s') || word.endsWith('sh') || word.endsWith('ch') || word.endsWith('x') || word.endsWith('z')) {
        plural += 'es';
    } else if (word.endsWith('y') && !word.match(/[aeiou]y$/i)) {
        plural = word.slice(0, -1) + 'ies';
    } else if (word.endsWith('f')) {
        plural = word.slice(0, -1) + 'ves';
    } else if (word.endsWith('fe')) {
        plural = word.slice(0, -2) + 'ves';
    } else {
        plural += 's';
    }

    return count === 1 || count === 0 ? word : plural;
}

export const isProduction = function(): boolean {
    return process.env.NODE_ENV == 'production';
}

export function getAdditionalInfoValue(key: string): DataResult<string> {
    const additionalInfo = store.state.configuration.additionalInfo as Array<KeyValue>;

    const found = additionalInfo.find(item => item.key === key);
  
    if (found) {
      return new DataResult(true, "", found.value);
    } else {
      return new DataResult(false, "", "");
    }
}

export function normalizeResourcePath(path: string): string {
    if (!path) {
        return "/";
    }

    const cleaned = path.trim().replace(/^\/+/, '');
    return `/${cleaned}`;
}

export function digitToWord(digit: number): string {
    if (!Number.isInteger(digit) || digit < 0 || digit > 9) {
      throw new Error(`Expected an integer 0–9, got ${digit}`);
    }

    const words = [ "zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine" ] as const;
    return words[digit];
  }

const updateLinkTag = function(rel: string, href: string) {
    const selector = `link[rel='${rel}']`;
    let link = document.querySelector(selector);

    if (link) {
      link.setAttribute('href', href);
    } else {
      link = document.createElement('link');
      link.setAttribute('rel', rel);
      link.setAttribute('href', href);
      document.head.appendChild(link);
    }
}

function getDecimalYearDifference(start: Date, end: Date): number {
    if (!(start instanceof Date) || !(end instanceof Date)) {
        throw new Error('Invalid date provided');
    }

    const diffInMilliseconds = end.getTime() - start.getTime();
    const millisecondsPerYear = 1000 * 60 * 60 * 24 * 365.25;
    const diffInYears = diffInMilliseconds / millisecondsPerYear;
    return diffInYears;
}
